import { motion, useIsPresent, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

import {
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  Skeleton,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { cn } from "@/lib/utils/cn";
import { FirmProfilePhoto } from "../FirmProfilePhoto/FirmProfilePhoto";
import { LawWorksLogo } from "../LawWorksLogo/LawWorksLogo";

export function SidebarSectionPanel({
  title,
  submenu,
}: {
  title?: string;
  submenu?: ReactNode;
}) {
  const { currentUser } = useUser();
  const isPresent = useIsPresent();
  const reduceMotion = useReducedMotion();

  return (
    <motion.section
      id="sidebar-section-panel"
      aria-label={title ? `${title} navigation` : "Loading navigation"}
      aria-hidden={!isPresent}
      {...(!isPresent ? { inert: "" } : {})}
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.15, ease: [0, 0, 0.2, 1] }}
      className="flex w-[calc(var(--sidebar-width)-var(--sidebar-width-icon))] shrink-0 flex-col"
    >
      <div className="flex h-14 shrink-0 items-center px-3">
        {currentUser.firm.profilePicturePresignedUrlKey ? (
          <FirmProfilePhoto
            firmName={currentUser.firm.name}
            firmProfilePicturePresignedUrlKey={
              currentUser.firm.profilePicturePresignedUrlKey
            }
            className="max-h-9 max-w-full"
            showFallbackLogo={false}
          />
        ) : (
          <LawWorksLogo className="h-auto w-full max-w-32" />
        )}
      </div>
      <SidebarContent
        className={cn(
          "gap-0 overflow-x-hidden overflow-y-auto px-2 py-3",
          "[&_[data-sidebar=menu-sub]]:m-0 [&_[data-sidebar=menu-sub]]:transform-none [&_[data-sidebar=menu-sub]]:gap-1 [&_[data-sidebar=menu-sub]]:border-0 [&_[data-sidebar=menu-sub]]:p-0",
          "[&_[data-sidebar=menu-sub]_[data-sidebar=menu-sub]]:border-sidebar-border [&_[data-sidebar=menu-sub]_[data-sidebar=menu-sub]]:my-1 [&_[data-sidebar=menu-sub]_[data-sidebar=menu-sub]]:ml-[3px] [&_[data-sidebar=menu-sub]_[data-sidebar=menu-sub]]:border-l [&_[data-sidebar=menu-sub]_[data-sidebar=menu-sub]]:pl-1",
          "[&_[data-sidebar=menu-sub-button]]:h-auto [&_[data-sidebar=menu-sub-button]]:min-h-9 [&_[data-sidebar=menu-sub-button]]:transform-none [&_[data-sidebar=menu-sub-button]]:gap-1.5 [&_[data-sidebar=menu-sub-button]]:px-1.5 [&_[data-sidebar=menu-sub-button]]:py-2 [&_[data-sidebar=menu-sub-button]]:text-[13px] [&_[data-sidebar=menu-sub-button]]:leading-5",
          "[&_[data-sidebar=menu-sub-button]>svg]:mr-0 [&_[data-sidebar=menu-sub-button]>svg]:size-4",
          "[&_[data-sidebar=menu-sub-button]>span:last-child]:[overflow-wrap:anywhere] [&_[data-sidebar=menu-sub-button]>span:last-child]:whitespace-normal",
        )}
      >
        {submenu ? (
          <SidebarMenu>
            <SidebarMenuItem>{submenu}</SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <Skeleton className="h-8 w-full rounded-md" />
        )}
      </SidebarContent>
      {currentUser.firm.profilePicturePresignedUrlKey ? (
        <SidebarFooter className="border-sidebar-border items-start gap-1 border-t px-3 py-3">
          <span className="text-muted-foreground text-[9px] tracking-wider uppercase">
            Powered by
          </span>
          <LawWorksLogo className="h-4 opacity-70" />
        </SidebarFooter>
      ) : null}
    </motion.section>
  );
}
